import fr from 'facet-rest';
import relationships from 'Consts/Relationships';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import { QueryVal, addQueryWithReplace, removeQuery } from 'Helpers/Query';
import { AchRelationshipResourceOverride } from 'Resources/Apex';
import { AccountCreationRequestSummaryOverride } from 'Overrides/AccountCreationRequest';

export const STATE_LOADING = 'loading';
export const STATE_SET_REFRESH_LINK = 'setRefreshLink';
export const STATE_SET_SUMMARY_COLLECTION = 'setSummaryCollection';
export const STATE_SET_ACCOUNT_SUMMARY_RESOURCE = 'setAccountSummaryResource';
export const STATE_SET_INVESTIGATION_RESOURCE = 'setInvestigationResource';
export const STATE_SET_ACH_RELATIONSHIP_RESOURCE = 'setAchRelationshipResource';

const URL_PARAM_INVESTIGATION_RESOURCE = 'investigationResource';
const URL_PARAM_ACCOUNT_SUMMARY_RESOURCE = 'accountSummaryResource';
const URL_PARAM_ACH_RELATIONSHIP_RESOURCE = 'achRelationshipResource';

//  LOCAL HELPERS
function addRemoveResourceSelfLinkURLQueryParam(queryParam, resource) {
  if (resource) {
    const url = resource.link('self').url();
    addQueryWithReplace({ [queryParam]: url });
  } else {
    removeQuery(queryParam);
  }
}
async function fetchResourceFromURLParam(param) {
  const linkURL = QueryVal(param);
  if (linkURL) {
    const collection = fr.collection(await fr.http.Get(linkURL));
    return collection.resources().head();
  }
  return null;
}

//  INIT
export async function initiallyFetchAccountRequestsCollection(userResource, dispatch, state) {
  try {
    const { accountSummaryCollection } = state;

    const linkURL = QueryVal('link');
    if (linkURL) {
      dispatch({ type: STATE_LOADING, loading: true });
      const collection = fr.collection(await fr.http.Get(linkURL));
      dispatch({ type: STATE_SET_SUMMARY_COLLECTION, collection });
    } else if (!accountSummaryCollection) {
      dispatch({ type: STATE_LOADING, loading: true });
      const collection = await userResource
        .relationship('accountRequestSummaries')
        .link('related')
        .Get();
      dispatch({ type: STATE_SET_SUMMARY_COLLECTION, collection });
    }
  } catch (err) {
    displayErrorMessage(err, err.errors);
  } finally {
    dispatch({ type: STATE_LOADING, loading: false });
  }
}

//  SET
export function setRefreshLink(link, dispatch) {
  dispatch({ type: STATE_SET_REFRESH_LINK, link });
}
export function setInvestigationResource(resource, dispatch) {
  dispatch({ type: STATE_SET_INVESTIGATION_RESOURCE, resource });
  addRemoveResourceSelfLinkURLQueryParam(URL_PARAM_INVESTIGATION_RESOURCE, resource);
}
export function setAccountSummaryResource(resource, dispatch) {
  dispatch({ type: STATE_SET_ACCOUNT_SUMMARY_RESOURCE, resource: resource ? resource.overrideMeta(AccountCreationRequestSummaryOverride) : null });
  addRemoveResourceSelfLinkURLQueryParam(URL_PARAM_ACCOUNT_SUMMARY_RESOURCE, resource);
}
export function setAchRelationshipResource(resource, dispatch) {
  dispatch({ type: STATE_SET_ACH_RELATIONSHIP_RESOURCE, resource });
  addRemoveResourceSelfLinkURLQueryParam(URL_PARAM_ACH_RELATIONSHIP_RESOURCE, resource);
}

//  REFRESH
export async function refreshAccountRequests(summaryUrl, dispatch) {
  try {
    dispatch({ type: STATE_LOADING, loading: true });
    const collection = fr.collection(await fr.http.Get(summaryUrl));
    dispatch({ type: STATE_SET_SUMMARY_COLLECTION, collection });
  } catch (err) {
    displayErrorMessage(err, err.errors);
  } finally {
    dispatch({ type: STATE_LOADING, loading: false });
  }
}
export async function refreshAccountSummaryResource(accountSummaryResource, dispatch, onSuccess = null) {
  try {
    dispatch({ type: STATE_LOADING, loading: true });
    const collection = await accountSummaryResource.link('self').Get();
    const resource = collection.resources()?.head();
    dispatch({ type: STATE_SET_ACCOUNT_SUMMARY_RESOURCE, resource: resource ? resource.overrideMeta(AccountCreationRequestSummaryOverride) : null });

    if (onSuccess) {
      onSuccess();
    }
  } catch (err) {
    displayErrorMessage(err, err.errors);
  } finally {
    dispatch({ type: STATE_LOADING, loading: false });
  }
}
export async function refreshInvestigationResource(investigationResource, dispatch, onSuccess = null) {
  try {
    dispatch({ type: STATE_LOADING, loading: true });
    const collection = await investigationResource.link('self').Get();
    const resource = collection.resources()?.head();
    dispatch({ type: STATE_SET_INVESTIGATION_RESOURCE, resource });

    if (onSuccess) {
      onSuccess();
    }
  } catch (err) {
    displayErrorMessage(err, err.errors);
  } finally {
    dispatch({ type: STATE_LOADING, loading: false });
  }
}
export async function refreshAchRelationshipResource(achRelationshipResource, dispatch, onSuccess) {
  try {
    dispatch({ type: STATE_LOADING, loading: true });
    const collection = await achRelationshipResource.link('self').Get();
    const resource = collection.overrideMeta(AchRelationshipResourceOverride).resources()?.head();
    dispatch({ type: STATE_SET_ACH_RELATIONSHIP_RESOURCE, resource });

    if (onSuccess) {
      onSuccess();
    }
  } catch (err) {
    displayErrorMessage(err, err.errors);
  } finally {
    dispatch({ type: STATE_LOADING, loading: false });
  }
}

//  SUBMIT
export async function submitInvestigationResource(investigationResource, dispatch) {
  try {
    dispatch({ type: STATE_LOADING, loading: true });
    const collection = await investigationResource.submit();
    const resource = collection.resources()?.head();
    dispatch({ type: STATE_SET_INVESTIGATION_RESOURCE, resource });
  } catch (err) {
    displayErrorMessage(err, err.errors);
  } finally {
    dispatch({ type: STATE_LOADING, loading: false });
  }
}

//  URL
export async function fetchResourcesFromURLParams(dispatch) {
  try {
    dispatch({ type: STATE_LOADING, loading: true });

    const investigation = await fetchResourceFromURLParam(URL_PARAM_INVESTIGATION_RESOURCE);
    if (investigation) {
      dispatch({ type: STATE_SET_INVESTIGATION_RESOURCE, resource: investigation });
    }

    const accountSummary = await fetchResourceFromURLParam(URL_PARAM_ACCOUNT_SUMMARY_RESOURCE);
    if (accountSummary) {
      dispatch({ type: STATE_SET_ACCOUNT_SUMMARY_RESOURCE, resource: accountSummary });
    }

    const achRelationship = await fetchResourceFromURLParam(URL_PARAM_ACH_RELATIONSHIP_RESOURCE);
    if (achRelationship) {
      dispatch({ type: STATE_SET_ACH_RELATIONSHIP_RESOURCE, resource: achRelationship });
    }
  } catch (err) {
    displayErrorMessage(err, err.errors);
  } finally {
    dispatch({ type: STATE_LOADING, loading: false });
  }
}
export async function fetchFromURLParamsFromWebhook(userResource, refreshLink, dispatch, onSuccess = null) {
  try {
    dispatch({ type: STATE_LOADING, loading: true });

    if (userResource
      && userResource.relationship(relationships.apexRefreshEvents)
      && userResource.relationship(relationships.apexRefreshAccountFundingEvents)) {
      await fr.http.Post(userResource.relationship(relationships.apexRefreshEvents).link('related').url());
      await fr.http.Post(userResource.relationship(relationships.apexRefreshAccountFundingEvents).link('related').url());
    }

    const investigation = await fetchResourceFromURLParam(URL_PARAM_INVESTIGATION_RESOURCE);
    if (investigation) {
      dispatch({ type: STATE_SET_INVESTIGATION_RESOURCE, resource: investigation });
    }

    const accountSummary = await fetchResourceFromURLParam(URL_PARAM_ACCOUNT_SUMMARY_RESOURCE);
    if (accountSummary) {
      dispatch({ type: STATE_SET_ACCOUNT_SUMMARY_RESOURCE, resource: accountSummary });
    }

    const achRelationship = await fetchResourceFromURLParam(URL_PARAM_ACH_RELATIONSHIP_RESOURCE);
    if (achRelationship) {
      dispatch({ type: STATE_SET_ACH_RELATIONSHIP_RESOURCE, resource: achRelationship });
    }

    //  Anytime we sync from webhooks, we want to refresh the summaries table to reflect it.
    await refreshAccountRequests(refreshLink, dispatch);

    if (onSuccess) {
      onSuccess();
    }
  } catch (err) {
    displayErrorMessage(err, err.errors);
  } finally {
    dispatch({ type: STATE_LOADING, loading: false });
  }
}
